import  { useState, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import { API_URL } from '../../utils/fatch';
import { useTripInfo } from "../../context";
import { GridLoader } from 'react-spinners';
import DOMPurify from 'dompurify';
import { useItinerary } from '../Itinerary';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';

import styles from './eachActivity.module.css';

// --- Review Card Component ---
const ReviewCard = ({ review }) => {
    const [expanded, setExpanded] = useState(false);
    const textLimit = 250;
    const isLongText = review.text?.length > textLimit;

    // Generate star display based on rating
    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(
                    <span key={i} className="material-symbols-outlined filled-star" style={{ color: 'var(--color-star)', fontSize: '16px' }}>
                        star
                    </span>
                );
            } else if (i === fullStars && hasHalfStar) {
                stars.push(
                    <span key={i} className="material-symbols-outlined filled-star" style={{ color: 'var(--color-star)', fontSize: '16px' }}>
                        star_half
                    </span>
                );
            } else {
                stars.push(
                    <span key={i} className="material-symbols-outlined" style={{ color: 'var(--color-border)', fontSize: '16px' }}>
                        star
                    </span>
                );
            }
        }
        return stars;
    };

    return (
        <div className={styles.reviewCard}>
            <div className={styles.reviewHeader}>
                <div className={styles.reviewerInfo}>
                    <img
                        src={review.profile_photo_url}
                        alt={review.author_name}
                        className={styles.reviewerAvatar}
                        onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=06a8f9&color=fff&size=80`;
                        }}
                    />
                    <div className={styles.reviewerDetails}>
                        <a
                            href={review.author_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.reviewerName}
                        >
                            {review.author_name}
                        </a>
                        <div className={styles.reviewMeta}>
                            <div className={styles.reviewStars}>
                                {renderStars(review.rating)}
                            </div>
                            <span className={styles.reviewTime}>{review.relative_time_description}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.reviewBody}>
                <p className={styles.reviewText}>
                    {expanded || !isLongText
                        ? review.text
                        : `${review.text.substring(0, textLimit)}...`}
                </p>
                {isLongText && (
                    <button
                        className={styles.expandBtn}
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? 'Show less' : 'Read more'}
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>
                            {expanded ? 'expand_less' : 'expand_more'}
                        </span>
                    </button>
                )}
            </div>
            {review.translated && (
                <div className={styles.translatedBadge}>
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>translate</span>
                    Translated from {review.original_language}
                </div>
            )}
        </div>
    );
};

// --- Reviews Section Component with Collapse ---
const ReviewsSection = ({ suggestion }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    
    if (!suggestion.reviews || suggestion.reviews.length === 0) {
        return null;
    }

    return (
        <div className={styles.card}>
            {/* Header with rating and collapse toggle */}
            <div 
                className={styles.reviewsSectionHeader}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className={styles.reviewsTitleRow}>
                    <h3 className={styles.cardTitle}>Reviews</h3>
                    <div className={styles.ratingBadge}>
                        <span className="material-symbols-outlined filled-star">star</span>
                        <span className={styles.ratingValue}>{suggestion.rating}</span>
                    </div>
                    <p className={styles.reviewCount}>
                        {(suggestion.user_ratings_total || suggestion.ratingCount)?.toLocaleString()} reviews
                    </p>
                </div>
                <button className={styles.collapseBtn} aria-label={isExpanded ? "Collapse reviews" : "Expand reviews"}>
                    <span className="material-symbols-outlined">
                        {isExpanded ? 'expand_less' : 'expand_more'}
                    </span>
                </button>
            </div>

            {/* Collapsible content */}
            <div className={`${styles.reviewsCollapsible} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                {/* See all reviews link */}
                {suggestion.url && (
                    <a 
                        href={suggestion.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.seeAllReviewsLink}
                        onClick={(e) => e.stopPropagation()}
                    >
                        See all reviews on Google Maps
                        <span className="material-symbols-outlined">open_in_new</span>
                    </a>
                )}

                {/* Reviews list */}
                <div className={styles.reviewsList}>
                    {suggestion.reviews.map((review, index) => (
                        <ReviewCard key={index} review={review} />
                    ))}
                </div>

                {/* Bottom button */}
                {suggestion.url && (
                    <a 
                        href={suggestion.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={styles.moreReviewsBtn}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                            alt="Google" 
                            className={styles.googleLogo}
                        />
                        Read more reviews on Google Maps
                        <span className="material-symbols-outlined">arrow_forward</span>
                    </a>
                )}
            </div>
        </div>
    );
};











export const DetailsForEachActivity = ({ isDarkMode = false }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // AI Travel Assistant state (card)
    const [aiLoading, setAiLoading] = useState(false);
    const [aiContent, setAiContent] = useState(null);
    const [aiError, setAiError] = useState(null);
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [aiQuestion, setAiQuestion] = useState('');

    // Floating Chatbot state
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef(null);

    // Itinerary hook
    const { addToItinerary, removeFromItinerary, isInItinerary } = useItinerary();

    const { place_id } = useParams();

    const navigate = useNavigate();

    // AI Travel Assistant - Discover handler
    const handleAiDiscover = async () => {
        if (aiLoading) return;
        
        if (aiContent) return setShowAiPanel(true);
        
        setAiLoading(true);
        setAiError(null);
        setShowAiPanel(true);

        try {
            const response = await fetch(`${API_URL}/ai-travel-assistant/discover`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    placeData: suggestion
                })
            });

            const data = await response.json();

            if (data.success) {
                setAiContent(data.content);
            } else {
                setAiError(data.error || 'Failed to get AI insights');
            }
        } catch (error) {
            console.error('AI Travel Assistant error:', error);
            setAiError('Failed to connect to AI assistant');
        } finally {
            setAiLoading(false);
        }
    };

    // AI Travel Assistant - Ask question handler (redirects to chatbot)
    const handleAiAsk = async (e) => {
        e.preventDefault();
        if (chatLoading || !aiQuestion.trim()) return;
        
        const question = aiQuestion.trim();
        setAiQuestion('');
        setChatOpen(true); // Open the chatbot
        
        // Send the message through chatbot
        setChatMessages(prev => [...prev, { role: 'user', content: question }]);
        setChatLoading(true);

        try {
            const chatHistory = chatMessages.slice(-15);
            
            const response = await fetch(`${API_URL}/ai-travel-assistant/ask`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    placeData: suggestion,
                    question: question,
                    chatHistory: chatHistory
                })
            });

            const data = await response.json();

            if (data.success) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
            } else {
                setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
            }
        } catch (error) {
            console.error('Chatbot error:', error);
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Failed to connect. Please try again.' }]);
        } finally {
            setChatLoading(false);
        }
    };

    // Scroll chat to bottom when new messages arrive
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages, chatLoading]);

    // Chatbot - Send message handler
    const handleChatSend = async (e) => {
        e.preventDefault();
        if (chatLoading || !chatInput.trim()) return;

        const userMessage = chatInput.trim();
        setChatInput('');
        
        // Add user message to chat
        const updatedMessages = [...chatMessages, { role: 'user', content: userMessage }];
        setChatMessages(updatedMessages);
        setChatLoading(true);

        try {
            // Send last 15 messages as chat history (excluding the current message)
            const chatHistory = chatMessages.slice(-15);
            
            const response = await fetch(`${API_URL}/ai-travel-assistant/ask`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    placeData: suggestion,
                    question: userMessage,
                    chatHistory: chatHistory
                })
            });

            const data = await response.json();

            if (data.success) {
                setChatMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
            } else {
                setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
            }
        } catch (error) {
            console.error('Chatbot error:', error);
            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Failed to connect. Please try again.' }]);
        } finally {
            setChatLoading(false);
        }
    };

    // Chatbot - Quick question handler
    const handleQuickQuestion = (question) => {
        setChatInput(question);
    };

    const { currentTripId, loadingUserInfo, firstName } = useTripInfo();
    const [suggestion, setSuggestion] = useState({});


    const [loading, setLoading] = useState(true);





    useEffect(() => {
        const getSuggestions = async () => {

            try {
                setLoading(true);

                const response = await fetch(`${API_URL}/suggestions/singel?place_id=${place_id}`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (!response.ok) {
                    console.warn('Error fetching suggestions');
                }

                const data = await response.json();
                console.log('Suggestions fetched:', data);
                setSuggestion(data.suggestion || {});


            } catch (error) {
                console.error('Error fetching suggestions:', error.message);
            } finally {
                setLoading(false);

            }


        };


        if (loadingUserInfo) return;
        if (!place_id) return;
        if (currentTripId) {

            getSuggestions();
        } else {
            navigate('/all_trips');

        }



    }, [currentTripId, loadingUserInfo, firstName]);

    // Helper for theme class
    const getThemeClass = () => (isDarkMode ? styles.dark : styles.light);

    // Transform images for lightbox
    const slides = useMemo(() => {
        if (!suggestion.photos || !Array.isArray(suggestion.photos)) return [];
        return suggestion.photos.map(photo => ({
            src: `${API_URL}/place-photo?photoRef=${photo.photo_reference}`,
            alt: `Gallery image ${photo.photo_reference}`,
            // Use the same src for thumbnail - the plugin will handle sizing
            thumbnail: `${API_URL}/place-photo?photoRef=${photo.photo_reference}`,
            attribution: photo.html_attributions?.[0] || ""
        }));
    }, [suggestion.photos]);


    // Gallery Logic
    const openGallery = (index = 0) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    return (
        loading ?
            <div className={styles.loadingIndicator}><GridLoader color="#d4ebfb" size={25} /></div>
            :
            <div className={`${styles.root} ${getThemeClass()}`}>
                <main className={styles.mainContent}>
                    <div className={styles.contentWrapper}>

                        {/* Back Button */}
                        <button 
                            className={styles.backButton}
                            onClick={() => navigate(-1)}
                            aria-label="Go back"
                        >
                            <span className="material-symbols-outlined">arrow_back</span>
                            <span>Back</span>
                        </button>

                        {/* Hero Grid with Gallery Trigger */}
                        {suggestion.photos ? suggestion.photos.length > 0 && (
                            <div className={styles.heroGrid}>
                                {/* Main Large Image */}
                                <div
                                    className={styles.heroMainImage}
                                    onClick={() => openGallery(0)}
                                    style={{ backgroundImage: `url("${API_URL}/place-photo?photoRef=${suggestion.photos[0]?.photo_reference}")` }}

                                ></div>

                                {/* Side Images */}
                                <div className={styles.heroSideImages}>
                                    {suggestion.photos[1] && (
                                        <div
                                            className={styles.heroSideImageTop}
                                            onClick={() => openGallery(1)}
                                            style={{ backgroundImage: `url("${API_URL}/place-photo?photoRef=${suggestion.photos[1].photo_reference}")` }}
                                        ></div>
                                    )}
                                    {suggestion.photos[2] && (
                                        <div
                                            className={styles.heroSideImageBottom}
                                            onClick={() => openGallery(2)}
                                            style={{ backgroundImage: `url("${API_URL}/place-photo?photoRef=${suggestion.photos[2].photo_reference}")` }}
                                        >
                                            <div className={styles.viewPhotosOverlay}>
                                                <button className={styles.viewPhotosBtn} onClick={(e) => { e.stopPropagation(); openGallery(0); }}>
                                                    View All Photos
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                        : (
                            <div className={styles.noImagePlaceholder}>
                                <img src="/no_img4.png" alt="no image available" style={{
    width: '100%',
    maxHeight: '520px',
    aspectRatio: '16/9', // Keeps it shaped like a standard video/photo
    objectFit: 'cover'
  }}/>
                            </div>
                        )}

                        {/* Title Section */}
                        <div className={styles.titleSection}>
                            <div className={styles.titleInfo}>
                                <h1 className={styles.pageTitle}>{suggestion.name}</h1>
                                
                                {/* Editorial Summary */}
                                {suggestion.editorial_summary?.overview && (
                                    <p className={styles.editorialSummary}>
                                        {suggestion.editorial_summary.overview}
                                    </p>
                                )}

                                {/* Location & Rating Row */}
                                <div className={styles.metaRow}>
                                    {suggestion.formatted_address && (
                                        <div className={styles.locationBadge}>
                                            <span className="material-symbols-outlined">location_on</span>
                                            <span>{suggestion.formatted_address}</span>
                                        </div>
                                    )}
                                    {suggestion.rating && (
                                        <div className={styles.ratingBadgeSmall}>
                                            <span className="material-symbols-outlined filled-star">star</span>
                                            <span>{suggestion.rating}</span>
                                            {suggestion.user_ratings_total && (
                                                <span className={styles.ratingCountSmall}>
                                                    ({suggestion.user_ratings_total.toLocaleString()})
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {suggestion.opening_hours?.open_now !== undefined && (
                                        <div className={`${styles.openStatus} ${suggestion.opening_hours.open_now ? styles.open : styles.closed}`}>
                                            <span className="material-symbols-outlined">
                                                {suggestion.opening_hours.open_now ? 'check_circle' : 'cancel'}
                                            </span>
                                            <span>{suggestion.opening_hours.open_now ? 'Open Now' : 'Closed'}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Tags (from types if available) */}
                                {suggestion.types && suggestion.types.length > 0 && (
                                    <div className={styles.tagList}>
                                        {suggestion.types.slice(0, 4).map((type, i) => (
                                            <div key={i} className={styles.tag}>
                                                {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button 
                             className={`${styles.addToItineraryBtn} ${isInItinerary(suggestion.place_id) ? styles.addToItineraryBtnActive : ''}`}
                                onClick={() => {
                                                console.warn('Toggling itinerary status for:',place_id);
                                                if (isInItinerary(suggestion.place_id)) {
                                                    removeFromItinerary(suggestion.place_id);
                                                } else {
                                                    addToItinerary(suggestion);
                                                }
                                            }}
                                            >
                                <span className="material-symbols-outlined">  {isInItinerary(suggestion.place_id) ? 'check_circle' : 'add_circle'}</span>
                                <span className={styles.truncate}>{isInItinerary(suggestion.place_id) ? 'Added' : 'Add to Trip'}</span>

                            </button>
                                
                        </div>

                        {/* Details Grid - changes layout when no reviews */}
                        {suggestion.reviews && suggestion.reviews.length > 0 ? (
                            // Has reviews - two column layout
                        <div className={styles.detailsGrid}>
                            {/* Left Column */}
                            <div className={styles.leftColumn}>
                                    <ReviewsSection suggestion={suggestion} />
                                </div>
                                {/* Right Column (Sidebar) */}
                                <div className={styles.rightColumn}>
                                    {/* AI Discover Card - FIRST */}
                                <div className={styles.aiCard}>
                                    <div className={styles.aiCardGlow}></div>
                                    <div className={styles.aiCardContent}>
                                        <div className={styles.aiCardHeader}>
                                            <div className={styles.aiIconWrapper}>
                                             <img src="/ai_assistant.svg" alt="" />
                                                    </div>
                                            <div>
                                                <h3 className={styles.aiCardTitle}>Discovery Engine</h3>
                                                <p className={styles.aiCardSubtitle}>RoamRites AI Travel Guide</p>
                                            </div>
                                        </div>
                                        
                                        {!showAiPanel ? (
                                            <button 
                                                className={styles.aiDiscoverBtn}
                                                onClick={handleAiDiscover}
                                                disabled={aiLoading}
                                            >
                                                <span className={styles.aiDiscoverIcon}>
                                                    <span className="material-symbols-outlined">
                                                        {aiLoading ? 'sync' : 'explore'}
                                                    </span>
                                                </span>
                                                <span className={styles.aiDiscoverText}>
                                                    <span>{aiLoading ? 'Researching...' : 'Explore with AI'}</span>
                                                    <span className={styles.aiDiscoverHint}>
                                                        {aiLoading ? 'Gathering travel insights' : 'Tips, history, best times & more'}
                                                    </span>
                                                </span>
                                                <span className="material-symbols-outlined">
                                                    {aiLoading ? '' : 'arrow_forward'}
                                                </span>
                                            </button>
                                        ) : (
                                            <div className={styles.aiResultPanel}>
                                                {aiLoading && (
                                                    <div className={styles.aiLoadingState}>
                                                        <div className={styles.aiLoadingSpinner}></div>
                                                        <p>AI is researching {suggestion.name}...</p>
                                                        <span>Searching web, analyzing reviews, gathering tips</span>
                                                    </div>
                                                )}
                                                
                                                {aiError && (
                                                    <div className={styles.aiErrorState}>
                                                        <span className="material-symbols-outlined">error</span>
                                                        <p>{aiError}</p>
                                                        <button onClick={handleAiDiscover}>Try Again</button>
                                                    </div>
                                                )}
                                                
                                                {aiContent && !aiLoading && (
                                                    <div className={styles.aiContentWrapper}>
                                                        <div className={styles.aiContentHeader}>
                                                            <span className="material-symbols-outlined">travel_explore</span>
                                                            <span>Travel Insights</span>
                                                            <button 
                                                                className={styles.aiCloseBtn}
                                                                onClick={() => setShowAiPanel(false)}
                                                            >
                                                                <span className="material-symbols-outlined">close</span>
                                                            </button>
                                                        </div>
                                                        <div 
                                                            className={styles.aiContent}
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    aiContent
                                                                        .replace(/## /g, '<h3>')
                                                                        .replace(/\n(?=<h3>)/g, '</p>')
                                                                        .replace(/<h3>([^<]+)/g, '<h3>$1</h3><p>')
                                                                        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                                                        .replace(/\n- /g, '</p><li>')
                                                                        .replace(/\n\n/g, '</p><p>')
                                                                        .replace(/\n/g, '<br/>')
                                                                        // Convert URLs to clickable links
                                                                        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'),
                                                                    { ADD_ATTR: ['target'] }
                                                                )
                                                            }}
                                                        />
                                                        
                                                        {/* Ask a Question */}
                                                        <form className={styles.aiAskForm} onSubmit={handleAiAsk}>
                                                            <div className={styles.aiAskInputWrapper}>
                                                                <span className="material-symbols-outlined">chat</span>
                                                                <input
                                                                    type="text"
                                                                    className={styles.aiAskInput}
                                                                    placeholder="Ask anything about this place..."
                                                                    value={aiQuestion}
                                                                    onChange={(e) => setAiQuestion(e.target.value)}
                                                                    disabled={aiLoading}
                                                                />
                                                                <button 
                                                                    type="submit" 
                                                                    className={styles.aiAskBtn}
                                                                    disabled={aiLoading || !aiQuestion.trim()}
                                                                >
                                                                    <span className="material-symbols-outlined">send</span>
                                                                </button>
                                    </div>
                                                            <div className={styles.aiQuickQuestions}>
                                                                <span>Try:</span>
                                                                <button type="button" onClick={() => setAiQuestion('Best time to visit?')}>Best time?</button>
                                                                <button type="button" onClick={() => setAiQuestion('What should I bring?')}>What to bring?</button>
                                                                <button type="button" onClick={() => setAiQuestion('Nearby food options?')}>Food nearby?</button>
                                                                <button 
                                                                    type="button" 
                                                                    className={styles.aiNewInsightsBtn}
                                                                    onClick={() => {
                                                                        setAiContent(null);
                                                                        handleAiDiscover();
                                                                    }}
                                                                >
                                                                    <span className="material-symbols-outlined">refresh</span>
                                                                    New insights
                                                                </button>
                                        </div>
                                                        </form>
                                        </div>
                                                )}
                                        </div>
                                        )}
                                </div>
                            </div>

                                {/* Location Card */}
                                <div className={styles.card}>
                                    <h3 className={styles.cardTitle}>Location</h3>
                                    <div className={styles.mapContainer}>
                                        {suggestion.geometry?.location ? (
                                                <iframe
                                                    className={styles.mapIframe}
                                                    title="Location Map"
                                                src={`https://maps.google.com/maps?q=${suggestion.geometry.location.lat},${suggestion.geometry.location.lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                                    allowFullScreen
                                                    loading="lazy"
                                                    referrerPolicy="no-referrer-when-downgrade"
                                                />
                                        ) : suggestion.formatted_address ? (
                                                        <iframe
                                                            className={styles.mapIframe}
                                                            title="Location Map"
                                                src={`https://maps.google.com/maps?q=${encodeURIComponent(suggestion.formatted_address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                                            allowFullScreen
                                                            loading="lazy"
                                                            referrerPolicy="no-referrer-when-downgrade"
                                                        />
                                            ) : (
                                            <div className={styles.mapPlaceholder}>
                                                <span className="material-symbols-outlined">map</span>
                                                <span>Map not available</span>
                                            </div>
                                            )}
                                    </div>

                                    <div className={styles.locationDetails}>
                                        {(suggestion.formatted_address || suggestion.address) && (
                                        <div className={styles.iconRow}>
                                            <span className="material-symbols-outlined">location_on</span>
                                                <p>{suggestion.formatted_address || suggestion.address}</p>
                                        </div>
                                        )}
                                        
                                        {suggestion.opening_hours?.weekday_text && suggestion.opening_hours.weekday_text.length > 0 && (
                                            <div className={styles.hoursSection}>
                                                <div className={styles.hoursHeader}>
                                            <span className="material-symbols-outlined">schedule</span>
                                                    <span>Hours</span>
                                                    <span className={`${styles.openBadge} ${suggestion.opening_hours?.open_now ? styles.open : styles.closed}`}>
                                                        {suggestion.opening_hours?.open_now ? 'Open' : 'Closed'}
                                                    </span>
                                                </div>
                                                <div className={styles.hoursList}>
                                                    {suggestion.opening_hours.weekday_text.map((day, index) => (
                                                        <p key={index} className={styles.hoursItem}>{day}</p>
                                                ))}
                                            </div>
                                        </div>
                                        )}
                                    </div>

                                    {suggestion.url && (
                                        <a 
                                            href={suggestion.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className={styles.directionsBtn}
                                        >
                                            <span className="material-symbols-outlined">directions</span>
                                            Get Directions
                                            <span className="material-symbols-outlined">arrow_forward</span>
                                        </a>
                                    )}
                                </div>

                                {/* Quick Actions Card */}
                                <div className={styles.actionsCard}>
                                    <div className={styles.actionsGrid}>
                                        {suggestion.website && (
                                            <a 
                                                href={suggestion.website} 
                                                className={styles.actionTile}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <div className={styles.actionTileIcon}>
                                                    <span className="material-symbols-outlined">language</span>
                                    </div>
                                                <span className={styles.actionTileLabel}>Website</span>
                                            </a>
                                        )}
                                        {suggestion.url && (
                                            <a 
                                                href={suggestion.url} 
                                                className={styles.actionTile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                                <div className={styles.actionTileIcon}>
                                                    <span className="material-symbols-outlined">map</span>
                                                </div>
                                                <span className={styles.actionTileLabel}>Maps</span>
                                            </a>
                                        )}
                                        <button 
                                            className={styles.actionTile}
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: suggestion.name,
                                                        text: suggestion.editorial_summary?.overview || `Check out ${suggestion.name}!`,
                                                        url: suggestion.url || window.location.href
                                                    }).catch(() => {});
                                                } else {
                                                    navigator.clipboard.writeText(suggestion.url || window.location.href);
                                                    alert('Link copied to clipboard!');
                                                }
                                            }}
                                        >
                                            <div className={styles.actionTileIcon}>
                                                <span className="material-symbols-outlined">share</span>
                                            </div>
                                            <span className={styles.actionTileLabel}>Share</span>
                                        </button>
                                        <button 
                                            className={`${styles.actionTile} ${isInItinerary(suggestion.place_id) ? styles.actionTileActive : ''}`}
                                            onClick={() => {
                                                console.warn('Toggling itinerary status for:',place_id);
                                                if (isInItinerary(suggestion.place_id)) {
                                                    removeFromItinerary(suggestion.place_id);
                                                } else {
                                                    addToItinerary(suggestion);
                                                }
                                            }}
                                        >
                                            <div className={styles.actionTileIcon}>
                                                <span className="material-symbols-outlined">
                                                    {isInItinerary(suggestion.place_id) ? 'check_circle' : 'add_circle'}
                                                </span>
                                            </div>
                                            <span className={styles.actionTileLabel} style={{ whiteSpace: 'nowrap' }}>
                                                {isInItinerary(suggestion.place_id) ? 'Added' : 'Add to Trip'}
                                        </span>
                                        </button>
                                    </div>
                                </div>
                                </div>
                            </div>
                        ) : (
                            // No reviews - single column balanced layout
                            <div className={styles.detailsGridNoReviews}>
                                {/* AI Discover Card - Full Version */}
                                <div className={styles.aiCard}>
                                    <div className={styles.aiCardGlow}></div>
                                    <div className={styles.aiCardContent}>
                                        <div className={styles.aiCardHeader}>
                                            <div className={styles.aiIconWrapper}>
                                                <img src="/ai_assistant.svg" alt="" />
                                            </div>
                                            <div>
                                                <h3 className={styles.aiCardTitle}>Discovery Engine</h3>
                                                <p className={styles.aiCardSubtitle}>RoamRites AI Travel Guide</p>
                                            </div>
                                        </div>
                                        
                                        {!showAiPanel ? (
                                            <button 
                                                className={styles.aiDiscoverBtn}
                                                onClick={handleAiDiscover}
                                                disabled={aiLoading}
                                            >
                                                <span className={styles.aiDiscoverIcon}>
                                                    <span className="material-symbols-outlined">
                                                        {aiLoading ? 'sync' : 'explore'}
                                                    </span>
                                                </span>
                                                <span className={styles.aiDiscoverText}>
                                                    <span>{aiLoading ? 'Researching...' : 'Explore with AI'}</span>
                                                    <span className={styles.aiDiscoverHint}>
                                                        {aiLoading ? 'Gathering travel insights' : 'Tips, history, best times & more'}
                                                    </span>
                                                </span>
                                                <span className="material-symbols-outlined">
                                                    {aiLoading ? '' : 'arrow_forward'}
                                                </span>
                                            </button>
                                        ) : (
                                            <div className={styles.aiResultPanel}>
                                                {aiLoading && (
                                                    <div className={styles.aiLoadingState}>
                                                        <div className={styles.aiLoadingSpinner}></div>
                                                        <p>AI is researching {suggestion.name}...</p>
                                                        <span>Searching web, analyzing reviews, gathering tips</span>
                                                    </div>
                                                )}
                                                
                                                {aiError && (
                                                    <div className={styles.aiErrorState}>
                                                        <span className="material-symbols-outlined">error</span>
                                                        <p>{aiError}</p>
                                                        <button onClick={handleAiDiscover}>Try Again</button>
                                                    </div>
                                                )}
                                                
                                                {aiContent && !aiLoading && (
                                                    <div className={styles.aiContentWrapper}>
                                                        <div className={styles.aiContentHeader}>
                                                            <span className="material-symbols-outlined">travel_explore</span>
                                                            <span>Travel Insights</span>
                                                            <button 
                                                                className={styles.aiCloseBtn}
                                                                onClick={() => setShowAiPanel(false)}
                                                            >
                                                                <span className="material-symbols-outlined">close</span>
                                                            </button>
                                                        </div>
                                                        <div 
                                                            className={styles.aiContent}
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                    aiContent
                                                                        .replace(/## /g, '<h3>')
                                                                        .replace(/\n(?=<h3>)/g, '</p>')
                                                                        .replace(/<h3>([^<]+)/g, '<h3>$1</h3><p>')
                                                                        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                                                        .replace(/\n- /g, '</p><li>')
                                                                        .replace(/\n\n/g, '</p><p>')
                                                                        .replace(/\n/g, '<br/>')
                                                                        .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'),
                                                                    { ADD_ATTR: ['target'] }
                                                                )
                                                            }}
                                                        />
                                                        
                                                        <form className={styles.aiAskForm} onSubmit={handleAiAsk}>
                                                            <div className={styles.aiAskInputWrapper}>
                                                                <span className="material-symbols-outlined">chat</span>
                                                                <input
                                                                    type="text"
                                                                    className={styles.aiAskInput}
                                                                    placeholder="Ask anything about this place..."
                                                                    value={aiQuestion}
                                                                    onChange={(e) => setAiQuestion(e.target.value)}
                                                                    disabled={aiLoading}
                                                                />
                                                                <button 
                                                                    type="submit" 
                                                                    className={styles.aiAskBtn}
                                                                    disabled={aiLoading || !aiQuestion.trim()}
                                                                >
                                                                    <span className="material-symbols-outlined">send</span>
                                                                </button>
                                                            </div>
                                                            <div className={styles.aiQuickQuestions}>
                                                                <span>Try:</span>
                                                                <button type="button" onClick={() => setAiQuestion('Best time to visit?')}>Best time?</button>
                                                                <button type="button" onClick={() => setAiQuestion('What should I bring?')}>What to bring?</button>
                                                                <button type="button" onClick={() => setAiQuestion('Nearby food options?')}>Food nearby?</button>
                                                                <button 
                                                                    type="button" 
                                                                    className={styles.aiNewInsightsBtn}
                                                                    onClick={() => {
                                                                        setAiContent(null);
                                                                        handleAiDiscover();
                                                                    }}
                                                                >
                                                                    <span className="material-symbols-outlined">refresh</span>
                                                                    New insights
                                                                </button>
                                                            </div>
                                                        </form>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Location Card */}
                                <div className={styles.card}>
                                    <h3 className={styles.cardTitle}>Location</h3>
                                    <div className={styles.mapContainer}>
                                        {suggestion.geometry?.location ? (
                                            <iframe
                                                className={styles.mapIframe}
                                                title="Location Map"
                                                src={`https://maps.google.com/maps?q=${suggestion.geometry.location.lat},${suggestion.geometry.location.lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                                allowFullScreen
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                            />
                                        ) : suggestion.formatted_address ? (
                                            <iframe
                                                className={styles.mapIframe}
                                                title="Location Map"
                                                src={`https://maps.google.com/maps?q=${encodeURIComponent(suggestion.formatted_address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                                allowFullScreen
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                            />
                                        ) : (
                                            <div className={styles.mapPlaceholder}>
                                                <span className="material-symbols-outlined">map</span>
                                                <span>Map not available</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.locationDetails}>
                                        {(suggestion.formatted_address || suggestion.address) && (
                                            <div className={styles.iconRow}>
                                                <span className="material-symbols-outlined">location_on</span>
                                                <p>{suggestion.formatted_address || suggestion.address}</p>
                                            </div>
                                        )}
                                    </div>
                                    {suggestion.url && (
                                        <a 
                                            href={suggestion.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer" 
                                            className={styles.directionsBtn}
                                        >
                                            <span className="material-symbols-outlined">directions</span>
                                            Get Directions
                                            <span className="material-symbols-outlined">arrow_forward</span>
                                        </a>
                                    )}
                                </div>

                                {/* Quick Actions Card */}
                                <div className={styles.actionsCard}>
                                    <div className={styles.actionsGrid}>
                                        {suggestion.website && (
                                            <a href={suggestion.website} className={styles.actionTile} target="_blank" rel="noopener noreferrer">
                                                <div className={styles.actionTileIcon}>
                                                    <span className="material-symbols-outlined">language</span>
                                                </div>
                                                <span className={styles.actionTileLabel}>Website</span>
                                            </a>
                                        )}
                                        {suggestion.url && (
                                            <a href={suggestion.url} className={styles.actionTile} target="_blank" rel="noopener noreferrer">
                                                <div className={styles.actionTileIcon}>
                                                    <span className="material-symbols-outlined">map</span>
                                                </div>
                                                <span className={styles.actionTileLabel}>Maps</span>
                                            </a>
                                        )}
                                        <button 
                                            className={styles.actionTile}
                                            onClick={() => {
                                                if (navigator.share) {
                                                    navigator.share({
                                                        title: suggestion.name,
                                                        text: suggestion.editorial_summary?.overview || `Check out ${suggestion.name}!`,
                                                        url: suggestion.url || window.location.href
                                                    }).catch(() => {});
                                                } else {
                                                    navigator.clipboard.writeText(suggestion.url || window.location.href);
                                                    alert('Link copied to clipboard!');
                                                }
                                            }}
                                        >
                                            <div className={styles.actionTileIcon}>
                                                <span className="material-symbols-outlined">share</span>
                                            </div>
                                            <span className={styles.actionTileLabel}>Share</span>
                                        </button>
                                        <button 
                                            className={`${styles.actionTile} ${isInItinerary(suggestion.place_id) ? styles.actionTileActive : ''}`}
                                            onClick={() => {
                                                if (isInItinerary(suggestion.place_id)) {
                                                    removeFromItinerary(suggestion.place_id);
                                                } else {
                                                    addToItinerary(suggestion);
                                                }
                                            }}
                                        >
                                            <div className={styles.actionTileIcon}>
                                                <span className="material-symbols-outlined">
                                                    {isInItinerary(suggestion.place_id) ? 'check_circle' : 'add_circle'}
                                                </span>
                                            </div>
                                            <span className={styles.actionTileLabel} style={{ whiteSpace: 'nowrap' }}>
                                                {isInItinerary(suggestion.place_id) ? 'Added' : 'Add to Trip'}
                                            </span>
                                        </button>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                </main>

                {/* Gallery Lightbox */}
                <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={lightboxIndex}
                    slides={slides}
                    plugins={[Thumbnails, Zoom, Slideshow, Counter]}

                    render={{
                        // Use slideFooter to ensure it updates per image
                        slideFooter: ({ slide }) => (
                            slide.attribution ? (
                                <div className={styles.attributionWrapper}>
                                    <div className={styles.attributionBox}>
                                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                                            person
                                        </span>
                                        <div
                                            className={styles.googleLink}
                                            dangerouslySetInnerHTML={{
                                                // Adds target="_blank" to the <a> tag automatically
                                                __html: slide.attribution.replace('<a ', '<a target="_blank" rel="noreferrer" ')
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null
                        ),
                    }}

                    thumbnails={{
                        position: 'bottom',
                        width: 100,
                        height: 70,
                        gap: 12,           // Space between thumbnails
                        imageFit: 'cover',
                        vignette: false    // Set to false to make thumbnails clearer
                    }}
                    // Custom styles for the lightbox overall
                    styles={{
                        container: { backgroundColor: "rgba(0, 0, 0, .9)" },
                        thumbnailsContainer: { padding: "10px 0" }
                    }}
                    counter={{
                        style: {
                            top: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)'
                        }
                    }}
                />

                {/* Floating Chatbot */}
                <div className={`${styles.chatbot} ${chatOpen ? styles.chatbotOpen : ''}`}>
                    {/* Chat Toggle Button */}
                    <button 
                        className={styles.chatbotToggle}
                        onClick={() => setChatOpen(!chatOpen)}
                        aria-label="Toggle chat"
                    >
                        {chatOpen ? (
                            <span className="material-symbols-outlined">close</span>
                        ) : (
                            <>
                                <img src="/ai_assistant.svg" alt="" className={styles.chatbotToggleIcon} />
                                {chatMessages.length === 0 && (
                                    <span className={styles.chatbotBadge}>Ask Me</span>
                                )}
                            </>
                        )}
                    </button>

                    {/* Chat Window */}
                    {chatOpen && (
                        <div className={styles.chatbotWindow}>
                            {/* Chat Header */}
                            <div className={styles.chatbotHeader}>
                                <div className={styles.chatbotHeaderInfo}>
                                    <img src="/ai_assistant.svg" alt="" className={styles.chatbotAvatar} />
                                    <div>
                                        <span className={styles.chatbotName}>RoamRite Travel Guide</span>
                                        <span className={styles.chatbotStatus}>
                                            <span className={styles.chatbotStatusDot}></span>
                                            Online
                                        </span>
                                    </div>
                                </div>
                                <button 
                                    className={styles.chatbotMinimize}
                                    onClick={() => setChatOpen(false)}
                                >
                                    <span className="material-symbols-outlined">remove</span>
                                </button>
                            </div>

                            {/* Chat Messages */}
                            <div className={styles.chatbotMessages}>
                                {chatMessages.length === 0 && (
                                    <div className={styles.chatbotWelcome}>
                                        <img src="/ai_assistant.svg" alt="" className={styles.chatbotWelcomeIcon} />
                                        <h4>Hi! I&apos;m your RoamRite Travel Guide</h4>
                                        <p>Ask me anything about {suggestion.name || 'this place'}!</p>
                                        <div className={styles.chatbotSuggestions}>
                                            <button onClick={() => handleQuickQuestion('Best time to visit?')}>
                                                🕐 Best time to visit?
                                            </button>
                                            <button onClick={() => handleQuickQuestion('What should I bring?')}>
                                                🎒 What to bring?
                                            </button>
                                            <button onClick={() => handleQuickQuestion('Local tips and tricks?')}>
                                                💡 Local tips?
                                            </button>
                                            <button onClick={() => handleQuickQuestion('Nearby restaurants?')}>
                                                🍽️ Food nearby?
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {chatMessages.map((msg, idx) => (
                                    <div 
                                        key={idx} 
                                        className={`${styles.chatbotBubble} ${msg.role === 'user' ? styles.chatbotBubbleUser : styles.chatbotBubbleAi}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <img src="/ai_assistant.svg" alt="" className={styles.chatbotBubbleAvatar} />
                                        )}
                                        <div className={styles.chatbotBubbleContent}>
                                            {msg.role === 'user' ? (
                                                <p>{msg.content}</p>
                                            ) : (
                                                <div 
                                                    dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(
                                                            msg.content
                                                                .replace(/## /g, '<strong>')
                                                                .replace(/\n(?=<strong>)/g, '</p>')
                                                                .replace(/<strong>([^<]+)/g, '<strong>$1</strong><br/>')
                                                                .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                                                                .replace(/\n- /g, '<br/>• ')
                                                                .replace(/\n\n/g, '<br/><br/>')
                                                                .replace(/\n/g, '<br/>')
                                                                // Convert URLs to clickable links
                                                                .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'),
                                                            { ADD_ATTR: ['target'] }
                                                        )
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {chatLoading && (
                                    <div className={`${styles.chatbotBubble} ${styles.chatbotBubbleAi}`}>
                                        <img src="/ai_assistant.svg" alt="" className={styles.chatbotBubbleAvatar} />
                                        <div className={styles.chatbotBubbleContent}>
                                            <div className={styles.chatbotTyping}>
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div ref={chatEndRef} />
                            </div>

                            {/* Chat Input */}
                            <form className={styles.chatbotInputArea} onSubmit={handleChatSend}>
                                <input
                                    type="text"
                                    className={styles.chatbotInput}
                                    placeholder="Type your question..."
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    disabled={chatLoading}
                                />
                                <button 
                                    type="submit" 
                                    className={styles.chatbotSendBtn}
                                    disabled={chatLoading || !chatInput.trim()}
                                >
                                    <span className="material-symbols-outlined">send</span>
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>
    );
};